/**
 * Liest die Task-spezifischen Manager-Rechte (Server-Autorität, DEC-14).
 *
 * Hintergrund: `AllowedActions` decken Ressourcen-Aktionen ab (Routine-Zeile:
 * Edit/Pause/Archive). Screen-Level-Rechte OHNE DTO-Träger — „+ Routine"
 * (`CanManageTaskRoutines`), Compliance-Zugang (`CanViewTaskCompliance`) und
 * PDF-Export (`CanExportTaskCompliance`, entspricht AllowedAction `ExportCompliance`)
 * — kommen aus `UserPermissionConfig`. Der getypte Frontend-Vertrag kennt den
 * `Tasks`-Block (noch) nicht; die Permissions werden als Rohobjekt geliefert, daher
 * lesen wir defensiv über `Tasks.*`. Fehlt der Block (Backend liefert ihn noch nicht),
 * ist das Recht `false` ⇒ strenger, server-treuer Default (Aktion versteckt).
 */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserPermissionsService } from 'src/app/modules/employee/services/user-permissions.service';

/** Rohform des (optionalen) Tasks-Rechte-Blocks. */
interface RawTasksPermissions {
  CanManageTaskRoutines?: boolean;
  CanViewTaskCompliance?: boolean;
  CanExportTaskCompliance?: boolean;
  CanViewOwnTasks?: boolean;
  CanViewTeamTasks?: boolean;
  CanViewAllTasks?: boolean;
}

@Injectable({ providedIn: 'root' })
export class TaskPermissionsService {
  constructor(private readonly userPermissions: UserPermissionsService) {}

  private get tasks(): RawTasksPermissions {
    const perms = this.userPermissions.CurrentUserPermissions as unknown as {
      Tasks?: RawTasksPermissions;
    };
    return perms?.Tasks ?? {};
  }

  /** Recht, Routine-Vorlagen anzulegen/zu bearbeiten (S10/S12 „+ Routine"). */
  canManageRoutines(): boolean {
    return this.tasks.CanManageTaskRoutines === true;
  }

  /**
   * Reaktive Variante von {@link canManageRoutines}. Rechte kommen asynchron
   * (BehaviorSubject) — daran koppeln, damit der „Routinen"-Tab nicht erst nach
   * einem synchronen Erst-Read (alles `false`) verzögert erscheint (F-05).
   */
  readonly canManageRoutines$: Observable<boolean> =
    this.userPermissions.currentUserPermissionsObservable.pipe(
      map(
        (perms) =>
          (perms as unknown as { Tasks?: RawTasksPermissions })?.Tasks
            ?.CanManageTaskRoutines === true,
      ),
    );

  /** Recht, das Compliance-Dashboard zu sehen (S11-Zugang, AllowedAction ViewCompliance). */
  canViewCompliance(): boolean {
    return this.tasks.CanViewTaskCompliance === true;
  }

  /**
   * Reaktive Variante von {@link canViewCompliance}. Rechte kommen asynchron
   * (BehaviorSubject) — daran koppeln, damit der „Compliance"-Tab auf jeder
   * Manager-Seite (Übersicht/Routinen) routen-unabhängig erscheint, sobald das
   * Recht eintrifft (GAP-01, analog zu {@link canManageRoutines$}).
   */
  readonly canViewCompliance$: Observable<boolean> =
    this.userPermissions.currentUserPermissionsObservable.pipe(
      map(
        (perms) =>
          (perms as unknown as { Tasks?: RawTasksPermissions })?.Tasks
            ?.CanViewTaskCompliance === true,
      ),
    );

  /** Recht, den Compliance-PDF-Export auszulösen (S11 „PDF-Export", ExportCompliance). */
  canExportCompliance(): boolean {
    return this.tasks.CanExportTaskCompliance === true;
  }

  /**
   * Manager-Scope (Team- oder Gesamt-Sicht). Gated Distribution-Deep-Links (S5), für die es
   * keine dedizierte Surface-Action gibt (Freeze §2.2) — server-treu aus dem Tasks-Rechteblock.
   */
  canViewManagerScope(): boolean {
    return this.tasks.CanViewTeamTasks === true || this.tasks.CanViewAllTasks === true;
  }
}
